import { GoogleOAuthProvider } from "@react-oauth/google";
import { Outlet } from "react-router-dom";

import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { I18nProvider } from "@/lib/i18n";

/**
 * Top-level providers — the moral equivalent of the old Next.js
 * ``app/layout.tsx``.
 *
 * Layering (outermost first):
 *   - GoogleOAuthProvider: makes the GIS client id available to every
 *     ``<GoogleLogin>`` button on the public surface. The id comes from
 *     the ``VITE_GOOGLE_OAUTH_CLIENT_ID`` env var; if it's missing we
 *     still render so non-auth surfaces work for local dev / smoke tests.
 *   - ThemeProvider: dark/light tokens (next-themes — pure React, kept).
 *   - I18nProvider: translations.
 *   - TooltipProvider: shadcn primitives need this once at the root.
 *
 * AuthProvider lives one layer down (in PublicShell / DashboardShell)
 * so the dashboard can hydrate it from a loader and the public surface
 * can keep ``state=null`` without re-fetching ``/v1/me``.
 */
export function AppShell() {
  // Vite exposes only ``import.meta.env.VITE_*`` variables to the client.
  // Empty string keeps GoogleOAuthProvider quiet during local smoke tests
  // where Google sign-in is not exercised.
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ?? "";

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <ThemeProvider>
        <I18nProvider>
          <TooltipProvider delayDuration={150}>
            <Outlet />
          </TooltipProvider>
        </I18nProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
